// Gang sheet geometry — everything in inches, origin at the top-left of the printable area.
export const SHEET_WIDTH_IN = 16.5;
export const PRINTABLE_WIDTH_IN = 15.5;
export const SPACING_IN = 0.5;
export const MIN_LENGTH_IN = 12;
export const MAX_LENGTH_IN = 600;
export const SNAP_IN = 0.25;
export const LENGTH_PRESETS = [12, 24, 36, 60, 120];

let seq = 0;
export const nextUid = () => `it_${Date.now().toString(36)}_${seq++}`;

export function aabb(item) {
    const rad = ((item.rot || 0) * Math.PI) / 180;
    const c = Math.abs(Math.cos(rad));
    const s = Math.abs(Math.sin(rad));
    return {
        w: item.wIn * c + item.hIn * s,
        h: item.wIn * s + item.hIn * c,
    };
}

export const snap = (v, grid = SNAP_IN) => Math.round(v / grid) * grid;

export function dpiOf(item) {
    if (!item.px?.w || !item.wIn) return null;
    return Math.floor(Math.min(item.px.w / item.wIn, item.px.h / item.hIn));
}

export function dpiLevel(dpi) {
    if (dpi == null) return "unknown";
    if (dpi >= 300) return "good";
    if (dpi >= 150) return "warn";
    return "bad";
}

export function contentLength(items) {
    return (items || []).reduce((m, it) => Math.max(m, it.y + aabb(it).h), 0);
}

export function clampItem(item, width = PRINTABLE_WIDTH_IN) {
    const b = aabb(item);
    const x = Math.min(Math.max(0, item.x), Math.max(0, width - b.w));
    const y = Math.max(0, item.y);
    return { ...item, x, y };
}

export function outOfBounds(items, width = PRINTABLE_WIDTH_IN, lengthIn = Infinity) {
    return (items || []).filter((it) => {
        const b = aabb(it);
        return it.x < -0.01 || it.y < -0.01 || it.x + b.w > width + 0.01 || it.y + b.h > lengthIn + 0.01;
    }).map((it) => it.uid);
}

// Two items conflict when their boxes come closer than the required spacing.
export function overlapping(items, spacing = SPACING_IN) {
    const bad = new Set();
    const boxes = (items || []).map((it) => {
        const b = aabb(it);
        return { uid: it.uid, x1: it.x - spacing / 2, y1: it.y - spacing / 2, x2: it.x + b.w + spacing / 2, y2: it.y + b.h + spacing / 2 };
    });
    for (let i = 0; i < boxes.length; i++) {
        for (let j = i + 1; j < boxes.length; j++) {
            const a = boxes[i], b = boxes[j];
            if (a.x1 < b.x2 - 0.01 && b.x1 < a.x2 - 0.01 && a.y1 < b.y2 - 0.01 && b.y1 < a.y2 - 0.01) {
                bad.add(a.uid); bad.add(b.uid);
            }
        }
    }
    return [...bad];
}

export function coverage(items, lengthIn, width = PRINTABLE_WIDTH_IN) {
    if (!lengthIn) return 0;
    const used = (items || []).reduce((n, it) => n + it.wIn * it.hIn, 0);
    return Math.min(100, Math.round((used / (width * lengthIn)) * 100));
}

// Shelf nesting: tallest first, filled row by row. Rotates 90° when that is the only way to fit.
export function autoBuild(items, { width = PRINTABLE_WIDTH_IN, spacing = SPACING_IN } = {}) {
    const list = (items || []).map((it) => {
        let next = { ...it };
        const b = aabb(next);
        if (b.w > width && next.hIn <= width) next = { ...next, rot: (next.rot || 0) + 90 };
        return next;
    }).sort((a, b) => aabb(b).h - aabb(a).h || aabb(b).w - aabb(a).w);

    let rowY = 0, rowX = 0, rowH = 0;
    const placed = list.map((it) => {
        const b = aabb(it);
        if (rowX > 0 && rowX + spacing + b.w > width + 0.01) {
            rowY += rowH + spacing;
            rowX = 0;
            rowH = 0;
        }
        const x = rowX === 0 ? 0 : rowX + spacing;
        rowX = x + b.w;
        rowH = Math.max(rowH, b.h);
        return { ...it, x: Math.round(x * 100) / 100, y: Math.round(rowY * 100) / 100 };
    });
    return placed;
}

// Repeat one item until the sheet (or targetLength) is packed.
export function autoFill(seed, existing, { width = PRINTABLE_WIDTH_IN, spacing = SPACING_IN, targetLength } = {}) {
    const b = aabb(seed);
    const perRow = Math.max(1, Math.floor((width + spacing) / (b.w + spacing)));
    const startY = contentLength(existing);
    const limit = targetLength || Math.max(MIN_LENGTH_IN, startY + b.h * 4);
    const copies = [];
    let y = existing.length ? startY + spacing : 0;
    while (y + b.h <= limit + 0.01 && copies.length < 400) {
        for (let c = 0; c < perRow; c++) {
            const x = c * (b.w + spacing);
            if (x + b.w > width + 0.01) break;
            copies.push({ ...seed, uid: nextUid(), x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 });
        }
        y += b.h + spacing;
    }
    return copies;
}

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;
export const feetLabel = (inches) => `${Math.floor(inches / 12)}′ ${Math.round(inches % 12)}″`;
